import { Component, Input, inject, output, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CatalogItemService } from 'src/app/services/catalog-item.service';
import { Paged } from 'src/app/models/shared/paged';
import { GetCatalogItem, GetCatalogItemVersion } from 'src/app/models/catalogs/catalog';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { ModalCatalogWizardComponent } from '../modal-catalog-wizard/modal-catalog-wizard.component';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs';
import { LoadingService } from 'src/app/services/loading.service';
import { CatalogDiffComponent } from './catalog-diff/catalog-diff.component';


@Component({
  selector: 'app-modal-catalog-versions',
  imports: [CommonModule, FormsModule, PaginationComponent, ModalCatalogWizardComponent, CatalogDiffComponent],
  templateUrl: './modal-catalog-versions.component.html',
  styleUrls: ['./modal-catalog-versions.component.css']
})
export class ModalCatalogVersionsComponent {
  changeData = output<void>();
  private catalogSvc = inject(CatalogItemService);
  loadingSvc = inject(LoadingService);
  itemHeader?: GetCatalogItem;

  filter: any = {
    page: 1,
    pageSize: 5,
    filterKey: '',
    filterValue: '',
    statusCode: '',
    versionNumber: null as number | null
  };

  totalItems = 0;
  versions: GetCatalogItemVersion[] = [];

  modalCatalogWizardComponent = viewChild(ModalCatalogWizardComponent);
  catalogDiffComponent = viewChild(CatalogDiffComponent);

  open(header: GetCatalogItem) {
    this.itemHeader = header;
    this.resetFilters();
    this.load();
    const modal = new (window as any).bootstrap.Modal('#modalCatalogVersions', {});
    modal.show();
  }

  close() {
    const el = document.getElementById('modalCatalogVersions');
    if (!el) return;
    const modal = (window as any).bootstrap.Modal.getInstance(el);
    modal?.hide();
  }

  resetFilters() {
    this.filter = {
      page: 1,
      pageSize: 5,
      filterKey: '',
      filterValue: '',
      statusCode: '',
      versionNumber: null
    };
  }

  load() {
    if (!this.itemHeader?.id) return;
    this.loadingSvc.show();
    const opts = {
      page: this.filter.page,
      pageSize: this.filter.pageSize,
      FilterKey: this.filter.filterKey || undefined,
      filterValue: this.filter.filterValue || undefined,
      statusCode: this.filter.statusCode || undefined,
      versionNumber: this.filter.versionNumber || undefined
    };
    this.catalogSvc.getCatalogItemVersions(this.itemHeader.id, opts)
      .pipe(
        finalize(() => this.loadingSvc.hide())
      )
      .subscribe({
        next: (res: Paged<GetCatalogItemVersion>) => {
          this.versions = res.items || [];
          this.totalItems = res.totalItems || 0;
        },
        error: (error) => {
          Swal.fire('Error', error[0] ?? 'Error al obtener los datos', 'error');
          this.versions = [];
          this.totalItems = 0;
        }
      });
  }

  onSearch() {
    this.filter.page = 1;
    this.load();
  }

  onPageChange(page: number) {
    this.filter.page = page;
    this.load();
  }

  onPageSizeChange(size: number) {
    this.filter.pageSize = size;
    this.filter.page = 1;
    this.load();
  }

  viewVersion(v: GetCatalogItemVersion) {
    const modalWizard = this.modalCatalogWizardComponent();
    modalWizard?.openForView(v.id);
  }

  editVersion(v: GetCatalogItemVersion) {
    const modalWizard = this.modalCatalogWizardComponent();
    modalWizard?.openForEdit(v.id);
  }


  publishVersion(v: GetCatalogItemVersion) {
    Swal.fire({
      title: '¿Deseas publicar esta versión?',
      text: `Se publicará la versión #${v.versionNumber} y se retirará la anterior.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, publicar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (result.isConfirmed) {
        this.loadingSvc.show();
        this.catalogSvc.publishCatalogItemVersion(v.catalogItemId, v.id)
          .pipe(
            finalize(() => this.loadingSvc.hide())
          )
          .subscribe({
            next: res => {
              Swal.fire({
                icon: "success",
                title: "Versión publicada exitosamente!",
                showConfirmButton: true,
                timer: 2000
              });
              this.changeData.emit();
              this.load();
              this.getCatalogItemById();
            },
            error: error => {
              Swal.fire({
                icon: "error",
                title: "Error al publicar la versión",
                showConfirmButton: true,
                text: error[0] ?? "Inténtalo de nuevo.",
              });
            }
          });
      }
    });
  }


  retireVersion(v: GetCatalogItemVersion) {
    Swal.fire({
      title: '¿Deseas retirar esta versión?',
      text: `Se retirará la versión #${v.versionNumber}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, retirar',
      cancelButtonText: 'Cancelar'
    }).then(result => {
      if (result.isConfirmed) {
        this.loadingSvc.show();
        this.catalogSvc.retireCatalogItemVersion(v.catalogItemId, v.id)
          .pipe(
            finalize(() => this.loadingSvc.hide())
          )
          .subscribe({
            next: res => {
              Swal.fire({
                icon: "success",
                title: "Versión retirada exitosamente!",
                showConfirmButton: true,
                timer: 2000
              });
              this.changeData.emit();
              this.load();
              this.getCatalogItemById();
            },
            error: error => {
              Swal.fire({
                icon: "error",
                title: "Error al retirar la versión",
                showConfirmButton: true,
                text: error[0] ?? "Inténtalo de nuevo.",
              });
            }
          });
      }
    });
  }

  getCatalogItemById() {
    if (!this.itemHeader) {
      return;
    }
    this.catalogSvc.getCatalogItemById(this.itemHeader.id)
      .subscribe({
        next: (res) => {
          this.itemHeader = res;
        }
      });
  }

  openDiffModal(v: GetCatalogItemVersion) {
    this.catalogDiffComponent()?.open(v.catalogItemId, v.id);
  }

}